import { useState, useEffect, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import axios from "axios";
import { formatDualCurrency } from "../config/fx";
import { 
  FileText,
  Send,
  Eye,
  CheckCircle2,
  XCircle,
  Clock,
  Package,
  MapPin
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || "https://api.afrigulfmarket.com";

const RFQPage = () => {
  const { t } = useTranslation();
  const { user, isAuthenticated } = useAuth();
  const [searchParams] = useSearchParams();
  
  const [categories, setCategories] = useState([]);
  const [myRfqs, setMyRfqs] = useState([]);
  const [allRfqs, setAllRfqs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('create');
  const [selectedRfq, setSelectedRfq] = useState(null);
  const [bids, setBids] = useState([]);
  const [showBidsModal, setShowBidsModal] = useState(false);
  const [showEditRfqModal, setShowEditRfqModal] = useState(false);
  const [bargainForms, setBargainForms] = useState({});
  const [bargainHistory, setBargainHistory] = useState({});
  const [bargainLoading, setBargainLoading] = useState({});
  const [editRfq, setEditRfq] = useState(null);
  const [editRfqForm, setEditRfqForm] = useState({
    category: '',
    specs: '',
    quantity: '',
    destination: ''
  });

  // Form state
  const [formData, setFormData] = useState({
    category: searchParams.get('category') || '',
    specs: '',
    quantity: '',
    destination: ''
  });

  // Bid form state
  const [bidForm, setBidForm] = useState({
    price_usd: '',
    lead_time_days: '',
    message: ''
  });

  const destinations = ['Kenya', 'Uganda', 'Tanzania', 'Ethiopia', 'Rwanda'];
  const autoOpenRef = useRef(false);

  useEffect(() => {
    fetchCategories();
    if (isAuthenticated) {
      fetchMyRfqs();
      if (user?.role === 'supplier' || user?.role === 'admin') {
        fetchAllRfqs();
      }
    }
  }, [isAuthenticated, user]);

  useEffect(() => {
    const rfqId = searchParams.get('rfq_id') || searchParams.get('rfqId') || searchParams.get('id');
    if (!rfqId || autoOpenRef.current) return;
    if (!isAuthenticated || !user) return;

    const rfqList = user.role === 'supplier' ? allRfqs : myRfqs;
    if (rfqList.length === 0) return;

    const match = rfqList.find((rfq) => String(rfq.id) === String(rfqId));
    if (!match) return;

    setActiveTab(user.role === 'supplier' ? 'available' : 'my-rfqs');
    openBidsModal(match);
    autoOpenRef.current = true;
  }, [searchParams, isAuthenticated, user, myRfqs, allRfqs]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/categories`);
      setCategories(response.data.categories);
    } catch (err) {
      console.error('Failed to fetch categories:', err);
    }
  };

  const fetchMyRfqs = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/rfqs/my/list`);
      setMyRfqs(response.data);
    } catch (err) {
      console.error('Failed to fetch my RFQs:', err);
    }
  };

  const fetchAllRfqs = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/rfqs?status=open`);
      setAllRfqs(response.data);
    } catch (err) {
      console.error('Failed to fetch RFQs:', err);
    }
  };

  const fetchBids = async (rfqId) => {
    try {
      const response = await axios.get(`${API_URL}/api/bids/rfq/${rfqId}`);
      setBids(response.data);
    } catch (err) {
      console.error('Failed to fetch bids:', err);
    }
  };

  const fetchBargainHistory = async (bidId) => {
    try {
      const response = await axios.get(`${API_URL}/api/bids/${bidId}/bargain`);
      setBargainHistory((prev) => ({ ...prev, [bidId]: response.data || [] }));
    } catch (err) {
      console.error('Failed to fetch bargain history:', err);
    }
  };

  useEffect(() => {
    if (!showBidsModal) {
      setBargainForms({});
      setBargainHistory({});
      return;
    }
    if (user?.role !== 'buyer' || bids.length === 0) return;

    const loadBargains = async () => {
      try {
        const responses = await Promise.all(
          bids.map((bid) => axios.get(`${API_URL}/api/bids/${bid.id}/bargain`))
        );
        const historyMap = {};
        bids.forEach((bid, index) => {
          historyMap[bid.id] = responses[index]?.data || [];
        });
        setBargainHistory(historyMap);
      } catch (err) {
        console.error('Failed to fetch bargain history:', err);
      }
    };

    loadBargains();
  }, [bids, showBidsModal, user]);

  useEffect(() => {
    if (!showBidsModal || user?.role !== 'buyer' || bids.length === 0) return;
    const intervalId = setInterval(() => {
      bids.forEach((bid) => fetchBargainHistory(bid.id));
    }, 5000);
    return () => clearInterval(intervalId);
  }, [bids, showBidsModal, user]);

  const updateBargainForm = (bidId, updates) => {
    setBargainForms((prev) => {
      const current = prev[bidId] || { price_usd: '', lead_time_days: '', message: '' };
      return { ...prev, [bidId]: { ...current, ...updates } };
    });
  };

  const formatBargainTimestamp = (bargain) => {
    const raw = bargain.created_at || bargain.createdAt || bargain.timestamp;
    if (!raw) return null;
    const date = new Date(raw);
    if (Number.isNaN(date.getTime())) return raw;
    return date.toLocaleString();
  };

  const handleSubmitRfq = async (e) => {
    e.preventDefault();
    if (!isAuthenticated) {
      toast.error('Please login to submit an RFQ');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/rfqs`, {
        ...formData,
        quantity: parseInt(formData.quantity)
      });
      toast.success('RFQ submitted successfully');
      setFormData({ category: '', specs: '', quantity: '', destination: '' });
      fetchMyRfqs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit RFQ');
    }
    setLoading(false);
  };

  const handleSubmitBid = async (rfqId) => {
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/bids`, {
        rfq_id: rfqId,
        price_usd: parseFloat(bidForm.price_usd),
        lead_time_days: parseInt(bidForm.lead_time_days),
        message: bidForm.message
      });
      toast.success('Bid submitted successfully');
      setBidForm({ price_usd: '', lead_time_days: '', message: '' });
      setShowBidsModal(false);
      fetchAllRfqs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit bid');
    }
    setLoading(false);
  };

  const handleAcceptBid = async (bidId) => {
    try {
      await axios.put(`${API_URL}/api/bids/${bidId}/status?status=accepted`);
      toast.success('Bid accepted');
      fetchMyRfqs();
      fetchBids(selectedRfq.id);
    } catch (err) {
      toast.error('Failed to accept bid');
    }
  };

  const handleRejectBid = async (bidId) => {
    try {
      await axios.put(`${API_URL}/api/bids/${bidId}/status?status=rejected`);
      toast.success('Bid rejected');
      fetchMyRfqs();
      fetchBids(selectedRfq.id);
    } catch (err) {
      toast.error('Failed to reject bid');
    }
  };

  const handleSubmitBargain = async (bidId) => {
    const form = bargainForms[bidId];
    if (!form?.price_usd || !form?.lead_time_days) return;
    setBargainLoading((prev) => ({ ...prev, [bidId]: true }));
    try {
      await axios.post(`${API_URL}/api/bids/${bidId}/bargain`, {
        price_usd: parseFloat(form.price_usd),
        lead_time_days: parseInt(form.lead_time_days),
        message: form.message
      });
      toast.success('Bargain sent');
      setBargainForms((prev) => ({
        ...prev,
        [bidId]: { price_usd: '', lead_time_days: '', message: '' }
      }));
      fetchBargainHistory(bidId);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send bargain');
    }
    setBargainLoading((prev) => ({ ...prev, [bidId]: false }));
  };

  const openEditRfqModal = (rfq) => {
    setEditRfq(rfq);
    setEditRfqForm({
      category: rfq.category || '',
      specs: rfq.specs || '',
      quantity: rfq.quantity?.toString() || '',
      destination: rfq.destination || ''
    });
    setShowEditRfqModal(true);
  };

  const handleUpdateRfq = async (e) => {
    e.preventDefault();
    if (!editRfq) return;
    setLoading(true);
    const updatePayload = {
      category: editRfqForm.category,
      specs: editRfqForm.specs,
      quantity: parseInt(editRfqForm.quantity),
      destination: editRfqForm.destination
    };
    try {
      await axios.put(`${API_URL}/api/rfqs/${editRfq.id}`, updatePayload);
      toast.success('RFQ updated');
      setShowEditRfqModal(false);
      setEditRfq(null);
      fetchMyRfqs();
      if (selectedRfq?.id === editRfq.id) {
        setSelectedRfq({ ...selectedRfq, ...updatePayload });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update RFQ');
    }
    setLoading(false);
  };

  const openBidsModal = (rfq) => {
    setSelectedRfq(rfq);
    fetchBids(rfq.id);
    setShowBidsModal(true);
  };

  const getStatusBadge = (status) => {
    const styles = {
      open: 'bg-[#10B981] hover:bg-[#10B981]',
      closed: 'bg-slate-500',
      awarded: 'bg-[#D4AF37]'
    };
    return (
      <Badge className={styles[status]}>
        {t(`rfq.status.${status}`)}
      </Badge>
    );
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="rfq-page">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="container-main py-8">
          <h1 className="text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {t('rfq.title')}
          </h1>
          <p className="text-slate-500">
            Submit your requirements and receive competitive bids from verified suppliers
          </p>
        </div>
      </div>

      <div className="container-main py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList>
            <TabsTrigger value="create">{t('rfq.create')}</TabsTrigger>
            {isAuthenticated && (
              <TabsTrigger value="my-rfqs">{t('rfq.myRfqs')}</TabsTrigger>
            )}
            {(user?.role === 'supplier' || user?.role === 'admin') && (
              <TabsTrigger value="available">Available RFQs</TabsTrigger>
            )}
          </TabsList>

          {/* Create RFQ Tab */}
          <TabsContent value="create">
            <Card className="card-base max-w-2xl">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-[#0D4F8B]" />
                  {t('rfq.create')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {!isAuthenticated ? (
                  <div className="text-center py-8">
                    <p className="text-slate-500 mb-4">Please login to submit an RFQ (Request for Quotation)</p>
                    <Link to="/login">
                      <Button className="bg-[#0D4F8B] hover:bg-[#0A3D6B]">
                        {t('nav.login')}
                      </Button>
                    </Link>
                  </div>
                ) : (
                  <form onSubmit={handleSubmitRfq} className="space-y-6">
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-2 block">
                        {t('rfq.category')} *
                      </label>
                      <Select 
                        value={formData.category} 
                        onValueChange={(v) => setFormData({ ...formData, category: v })}
                      >
                        <SelectTrigger data-testid="rfq-category" aria-label="RFQ category">
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                        <SelectContent>
                          {categories.map((cat) => (
                            <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-2 block">
                        {t('rfq.specs')} *
                      </label>
                      <Textarea
                        placeholder="Describe your product requirements in detail..."
                        value={formData.specs}
                        onChange={(e) => setFormData({ ...formData, specs: e.target.value })}
                        aria-label="RFQ specifications"
                        rows={4}
                        className="input-style"
                        data-testid="rfq-specs"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-slate-700 mb-2 block">
                          {t('rfq.quantity')} *
                        </label>
                        <Input
                          type="number"
                          placeholder="Enter quantity"
                          value={formData.quantity}
                          onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                          aria-label="RFQ quantity"
                          className="input-style"
                          data-testid="rfq-quantity"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-slate-700 mb-2 block">
                          {t('rfq.destination')} *
                        </label>
                        <Select 
                          value={formData.destination} 
                          onValueChange={(v) => setFormData({ ...formData, destination: v })}
                        >
                          <SelectTrigger data-testid="rfq-destination" aria-label="RFQ destination">
                            <SelectValue placeholder="Select destination" />
                          </SelectTrigger>
                          <SelectContent>
                            {destinations.map((dest) => (
                              <SelectItem key={dest} value={dest}>{dest}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <Button 
                      type="submit" 
                      className="w-full bg-[#10B981] hover:bg-[#059669]"
                      disabled={loading || !formData.category || !formData.specs || !formData.quantity || !formData.destination}
                      data-testid="submit-rfq-btn"
                    >
                      <Send className="w-4 h-4 mr-2" />
                      {t('rfq.submit')}
                    </Button>
                  </form>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* My RFQs Tab */}
          <TabsContent value="my-rfqs">
            {myRfqs.length === 0 ? (
              <Card className="card-base">
                <CardContent className="p-8 text-center">
                  <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No RFQs yet. Create your first RFQ!</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {myRfqs.map((rfq) => (
                  <Card key={rfq.id} className="card-base" data-testid={`my-rfq-${rfq.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-3">
                        <Badge variant="outline">{rfq.category}</Badge>
                        {getStatusBadge(rfq.status)}
                      </div>
                      <p className="text-sm text-slate-600 line-clamp-2 mb-3">{rfq.specs}</p>
                      <div className="flex items-center gap-4 text-xs text-slate-500 mb-4">
                        <span className="flex items-center gap-1">
                          <Package className="w-3 h-3" />
                          {rfq.quantity} units
                        </span>
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {rfq.destination}
                        </span>
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="w-full"
                        onClick={() => openBidsModal(rfq)}
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        {t('rfq.viewBids')}
                      </Button>
                      {rfq.status === 'open' && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full mt-2"
                          onClick={() => openEditRfqModal(rfq)}
                        >
                          Edit
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Available RFQs Tab (for suppliers) */}
          <TabsContent value="available">
            {allRfqs.length === 0 ? (
              <Card className="card-base">
                <CardContent className="p-8 text-center">
                  <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No open RFQs available</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {allRfqs.map((rfq) => (
                  <Card key={rfq.id} className="card-base" data-testid={`available-rfq-${rfq.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-3">
                        <Badge variant="outline">{rfq.category}</Badge>
                        {getStatusBadge(rfq.status)}
                      </div>
                      <p className="text-sm text-slate-600 line-clamp-2 mb-3">{rfq.specs}</p>
                      <div className="flex items-center gap-4 text-xs text-slate-500 mb-2">
                        <span className="flex items-center gap-1">
                          <Package className="w-3 h-3" />
                          {rfq.quantity} units
                        </span>
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {rfq.destination}
                        </span>
                      </div>
                      {rfq.buyer_name && (
                        <p className="text-xs text-slate-400 mb-4">By: {rfq.buyer_name}</p>
                      )}
                      <Button 
                        size="sm" 
                        className="w-full bg-[#10B981] hover:bg-[#059669]"
                        onClick={() => { setSelectedRfq(rfq); setShowBidsModal(true); }}
                      >
                        {t('bids.submit')}
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Edit RFQ Modal */}
      <Dialog open={showEditRfqModal} onOpenChange={setShowEditRfqModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit RFQ</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdateRfq} className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block">
                {t('rfq.category')} *
              </label>
              <Select
                value={editRfqForm.category}
                onValueChange={(v) => setEditRfqForm({ ...editRfqForm, category: v })}
              >
                <SelectTrigger aria-label="Edit RFQ category">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block">
                {t('rfq.specs')} *
              </label>
              <Textarea
                placeholder="Describe your product requirements in detail..."
                value={editRfqForm.specs}
                onChange={(e) => setEditRfqForm({ ...editRfqForm, specs: e.target.value })}
                rows={4}
                className="input-style"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-2 block">
                  {t('rfq.quantity')} *
                </label>
                <Input
                  type="number"
                  placeholder="Enter quantity"
                  value={editRfqForm.quantity}
                  onChange={(e) => setEditRfqForm({ ...editRfqForm, quantity: e.target.value })}
                  className="input-style"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-2 block">
                  {t('rfq.destination')} *
                </label>
                <Select
                  value={editRfqForm.destination}
                  onValueChange={(v) => setEditRfqForm({ ...editRfqForm, destination: v })}
                >
                  <SelectTrigger aria-label="Edit RFQ destination">
                    <SelectValue placeholder="Select destination" />
                  </SelectTrigger>
                  <SelectContent>
                    {destinations.map((dest) => (
                      <SelectItem key={dest} value={dest}>{dest}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button
              type="submit"
              className="w-full bg-[#10B981] hover:bg-[#059669]"
              disabled={loading || !editRfqForm.category || !editRfqForm.specs || !editRfqForm.quantity || !editRfqForm.destination}
            >
              Save Changes
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Bids Modal */}
      <Dialog open={showBidsModal} onOpenChange={setShowBidsModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {(user?.role === 'supplier' || user?.role === 'admin') ? 'Submit Bid' : 'Bids for RFQ'}
            </DialogTitle>
          </DialogHeader>
          
          {selectedRfq && (
            <div className="space-y-6">
              {/* RFQ Summary */}
              <Card className="bg-slate-50">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge variant="outline">{selectedRfq.category}</Badge>
                    {getStatusBadge(selectedRfq.status)}
                  </div>
                  <p className="text-sm text-slate-600">{selectedRfq.specs}</p>
                  <div className="flex items-center gap-4 text-sm text-slate-500 mt-2">
                    <span>Qty: {selectedRfq.quantity}</span>
                    <span>Dest: {selectedRfq.destination}</span>
                  </div>
                </CardContent>
              </Card>

              {/* Submit Bid Form (for suppliers) */}
              {(user?.role === 'supplier' || user?.role === 'admin') && selectedRfq.status === 'open' && (
                <div className="space-y-4">
                  <h4 className="font-semibold">Your Bid</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-2 block">
                        {t('bids.price')} *
                      </label>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="0.00"
                        value={bidForm.price_usd}
                        onChange={(e) => setBidForm({ ...bidForm, price_usd: e.target.value })}
                        data-testid="bid-price"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-2 block">
                        {t('bids.leadTime')} *
                      </label>
                      <Input
                        type="number"
                        placeholder="Days"
                        value={bidForm.lead_time_days}
                        onChange={(e) => setBidForm({ ...bidForm, lead_time_days: e.target.value })}
                        data-testid="bid-leadtime"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-2 block">
                      {t('bids.message')}
                    </label>
                    <Textarea
                      placeholder="Additional details about your offer..."
                      value={bidForm.message}
                      onChange={(e) => setBidForm({ ...bidForm, message: e.target.value })}
                      rows={3}
                      data-testid="bid-message"
                    />
                  </div>
                  <Button 
                    onClick={() => handleSubmitBid(selectedRfq.id)}
                    disabled={loading || !bidForm.price_usd || !bidForm.lead_time_days}
                    className="w-full bg-[#10B981] hover:bg-[#059669]"
                    data-testid="submit-bid-btn"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    {t('bids.submit')}
                  </Button>
                </div>
              )}

              {/* Bids List (for buyers) */}
              {user?.role === 'buyer' && (
                <div className="space-y-4">
                  <h4 className="font-semibold">{t('bids.title')} ({bids.length})</h4>
                  {bids.length === 0 ? (
                    <p className="text-sm text-slate-500">{t('rfq.noBids')}</p>
                  ) : (
                    <div className="space-y-3">
                      {bids.map((bid) => (
                        <Card key={bid.id} className="card-base">
                          <CardContent className="p-4">
                            <Tabs defaultValue="details" className="space-y-3">
                              <TabsList className="bg-slate-100">
                                <TabsTrigger value="details">Details</TabsTrigger>
                                <TabsTrigger value="bargain">Bargain</TabsTrigger>
                              </TabsList>
                              <TabsContent value="details" className="space-y-3">
                                <div className="flex items-center justify-between">
                                  <span className="font-medium">{bid.supplier_name}</span>
                                  <Badge className={
                                    bid.status === 'accepted' ? 'bg-[#10B981]' :
                                    bid.status === 'rejected' ? 'bg-red-500' : 'bg-slate-500'
                                  }>
                                    {t(`bids.${bid.status}`)}
                                  </Badge>
                                </div>
                                <div className="flex items-center gap-4 text-sm">
                                  <span className="font-bold text-[#0D4F8B]">
                                    {formatDualCurrency(bid.price_usd).usd}
                                  </span>
                                  <span className="text-xs text-slate-500">
                                    {formatDualCurrency(bid.price_usd).aed}
                                  </span>
                                  <span className="flex items-center gap-1 text-slate-500">
                                    <Clock className="w-4 h-4" />
                                    {bid.lead_time_days} days
                                  </span>
                                </div>
                                {bid.message && (
                                  <p className="text-sm text-slate-600">{bid.message}</p>
                                )}
                                {bid.status === 'pending' && selectedRfq.status === 'open' && (
                                  <div className="flex gap-2">
                                    <Button 
                                      size="sm" 
                                      className="bg-[#10B981] hover:bg-[#059669]"
                                      onClick={() => handleAcceptBid(bid.id)}
                                    >
                                      <CheckCircle2 className="w-4 h-4 mr-1" />
                                      {t('bids.accept')}
                                    </Button>
                                    <Button 
                                      size="sm" 
                                      className="bg-red-500 hover:bg-red-600"
                                      onClick={() => handleRejectBid(bid.id)}
                                    >
                                      <XCircle className="w-4 h-4 mr-1" />
                                      {t('bids.reject')}
                                    </Button>
                                  </div>
                                )}
                                <div className="border-t border-slate-200 pt-3">
                                  <h5 className="text-xs font-semibold text-slate-500 uppercase">
                                    Bargain History
                                  </h5>
                                  {(bargainHistory[bid.id] || []).length === 0 ? (
                                    <p className="text-xs text-slate-500 mt-2">No bargains yet.</p>
                                  ) : (
                                    <div className="space-y-2 mt-2">
                                      {(bargainHistory[bid.id] || []).map((entry, index) => {
                                        const timestamp = formatBargainTimestamp(entry);
                                        return (
                                          <div key={entry.id || entry.created_at || index} className="rounded-md border border-slate-200 bg-white p-2">
                                            <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
                                              {entry.price_usd !== undefined && (
                                                <>
                                                  <span className="font-semibold text-slate-700">
                                                    {formatDualCurrency(entry.price_usd).usd}
                                                  </span>
                                                  <span>{formatDualCurrency(entry.price_usd).aed}</span>
                                                </>
                                              )}
                                              {entry.lead_time_days !== undefined && (
                                                <span>{entry.lead_time_days} days</span>
                                              )}
                                              {timestamp && <span>{timestamp}</span>}
                                            </div>
                                            {entry.message && (
                                              <p className="text-xs text-slate-600 mt-1">{entry.message}</p>
                                            )}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              </TabsContent>
                              <TabsContent value="bargain" className="space-y-3">
                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <label className="text-xs font-medium text-slate-600 mb-1 block">
                                      Price (USD) *
                                    </label>
                                    <Input
                                      type="number"
                                      step="0.01"
                                      placeholder="0.00"
                                      value={(bargainForms[bid.id] || {}).price_usd || ''}
                                      onChange={(e) => updateBargainForm(bid.id, { price_usd: e.target.value })}
                                    />
                                  </div>
                                  <div>
                                    <label className="text-xs font-medium text-slate-600 mb-1 block">
                                      Lead time (days) *
                                    </label>
                                    <Input
                                      type="number"
                                      placeholder="Days"
                                      value={(bargainForms[bid.id] || {}).lead_time_days || ''}
                                      onChange={(e) => updateBargainForm(bid.id, { lead_time_days: e.target.value })}
                                    />
                                  </div>
                                </div>
                                <div>
                                  <label className="text-xs font-medium text-slate-600 mb-1 block">
                                    Message
                                  </label>
                                  <Textarea
                                    placeholder="Share your counter offer details..."
                                    rows={3}
                                    value={(bargainForms[bid.id] || {}).message || ''}
                                    onChange={(e) => updateBargainForm(bid.id, { message: e.target.value })}
                                  />
                                </div>
                                <Button
                                  size="sm"
                                  className="bg-[#0D4F8B] hover:bg-[#0A3D6B]"
                                  disabled={
                                    bargainLoading[bid.id] ||
                                    !(bargainForms[bid.id]?.price_usd) ||
                                    !(bargainForms[bid.id]?.lead_time_days)
                                  }
                                  onClick={() => handleSubmitBargain(bid.id)}
                                >
                                  Send Bargain
                                </Button>
                              </TabsContent>
                            </Tabs>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RFQPage;

